/** @format */
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { Transition } from '@mpb-com/feather/components/atoms/transition';
import { useClickOutside, useKeyPress } from '@mpb-com/feather/hooks';
import React, {
    FunctionComponent,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react';
import styled from 'styled-components';

import CloseIcon from '@toucan/client/global/icons/close.svg';

import BtnCurve from '@toucan/client/global/images/modal/btn-curve.svg';

import {
    ApplicationContext,
    ScrollLock,
    useFocusTrap,
} from '@toucan/client/global/application';

import { DEFAULT_TEST_ID } from './constants';
import { styles } from './tray.styles';
import { TrayInnerProps, TrayProps } from './types';

const StyledTray = styled.div`
    ${styles}
`;

const TrayInner: FunctionComponent<TrayInnerProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    children,
    isAnimating,
    ...otherProps
}) => {
    const { addPageScrollLock, setTrayVisible } =
        useContext(ApplicationContext);
    const bodyRef = useRef(
        typeof document !== 'undefined' ? document.body : null
    );
    const trayRef = useRef<HTMLDivElement>(null);

    useFocusTrap(trayRef);

    const closeTray = useCallback(() => {
        setTrayVisible(false);
    }, [setTrayVisible]);

    useKeyPress(bodyRef.current, ['Escape', 'Esc'], () => {
        closeTray();
    });

    useClickOutside([trayRef], closeTray);

    useEffect(() => {
        addPageScrollLock(ScrollLock.TRAY);
    }, [addPageScrollLock]);

    useEffect(() => {
        window.addEventListener('popstate', closeTray);

        return () => {
            window.removeEventListener('popstate', closeTray);
        };
    }, [closeTray]);

    return (
        <StyledTray
            data-testid={dataTestId}
            ref={trayRef}
            $isAnimating={isAnimating}
            {...otherProps}
        >
            <div className="close-button-curve">
                <BtnCurve />
            </div>
            <Icon
                data-testid={`${dataTestId}__close-button`}
                className="close-button"
                variant={IconVariant.NAVIGATION}
                size={IconSize.M}
                onClick={closeTray}
            >
                <CloseIcon />
            </Icon>
            <div
                data-testid={`${dataTestId}__content`}
                className="tray-content"
            >
                {children}
            </div>
        </StyledTray>
    );
};

export const Tray: FunctionComponent<TrayProps> = ({
    isVisible,
    children,
    ...otherProps
}) => {
    const { removePageScrollLock } = useContext(ApplicationContext);

    useEffect(() => {
        if (!isVisible) {
            removePageScrollLock(ScrollLock.TRAY);
        }
    }, [isVisible, removePageScrollLock]);

    return (
        <Transition durationMs={300} isVisible={isVisible}>
            {({ props, isAnimating }) => (
                <TrayInner {...otherProps} isAnimating={isAnimating} {...props}>
                    {children}
                </TrayInner>
            )}
        </Transition>
    );
};
